import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

// Constants moved to a separate object for better organization
const DATE_CONFIG = {
    currentDate: new Date(),
    startYear: 2000,
    yearsToShow: 32,

    get currentYear() {
        return this.currentDate.getFullYear();
    },

    get currentMonth() {
        return this.currentDate.getMonth() + 1;
    },

    get currentDay() {
        return this.currentDate.getDate();
    },

    // Month names array to avoid moment.js dependency
    months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ],
};

// Helper functions
const padNumber = (num) => num.toString().padStart(2, "0");

// Function to get the number of days in a specific month and year
const getDaysInMonth = (year, month) => {
    // If month is "all", return 31 days as a default
    if (month === "all") return 31;

    // Create a date for the first day of the next month, then subtract one day to get the last day of the current month
    return new Date(year, month, 0).getDate();
};

const createDaysOptions = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const options = Array.from({ length: daysInMonth }, (_, i) => ({
        value: i + 1,
        label: padNumber(i + 1),
    }));

    return [{ value: "all", label: "All" }, ...options];
};

const monthsOptions = [
    { value: "all", label: "All" },
    ...DATE_CONFIG.months.map((month, index) => ({
        value: index + 1,
        label: month,
    })),
];

const yearsOptions = Array.from(
    { length: DATE_CONFIG.yearsToShow },
    (_, i) => ({
        value: DATE_CONFIG.startYear + i,
        label: (DATE_CONFIG.startYear + i).toString(),
    })
);

const MachineStatusFilter = ({ setFilter, onClose }) => {
    const { handleSubmit, control, setValue, watch } = useForm({
        defaultValues: {
            day: {
                value: DATE_CONFIG.currentDay,
                label: padNumber(DATE_CONFIG.currentDay),
            },
            month: {
                value: DATE_CONFIG.currentMonth,
                label: DATE_CONFIG.months[DATE_CONFIG.currentMonth - 1],
            },
            year: {
                value: DATE_CONFIG.currentYear,
                label: DATE_CONFIG.currentYear.toString(),
            },
        },
    });

    const selectedDay = watch("day");
    const selectedMonth = watch("month");
    const selectedYear = watch("year");

    // Dynamically generate days options based on selected month and year
    const daysOptions = useMemo(() => {
        const year = selectedYear?.value || DATE_CONFIG.currentYear;
        const month = selectedMonth?.value || DATE_CONFIG.currentMonth;
        return createDaysOptions(year, month);
    }, [selectedMonth, selectedYear]);

    // Auto-select current month and adjust days if needed
    useEffect(() => {
        if (selectedDay?.value !== "all" && selectedMonth?.value === "all") {
            setValue("month", {
                value: DATE_CONFIG.currentMonth,
                label: DATE_CONFIG.months[DATE_CONFIG.currentMonth - 1],
            });
        }

        // If selected day is not valid for the current month, reset to "all"
        if (selectedMonth && selectedYear) {
            const daysInMonth = getDaysInMonth(
                selectedYear.value,
                selectedMonth.value
            );
            if (
                selectedDay &&
                selectedDay.value !== "all" &&
                selectedDay.value > daysInMonth
            ) {
                setValue("day", { value: "all", label: "All" });
            }
        }
    }, [selectedDay, selectedMonth, selectedYear, setValue]);

    const formatFilterString = (day, month, year) => {
        if (!day && !month) return year.value.toString();
        if (!day) return `${padNumber(month.value)}-${year.value}`;
        return `${padNumber(day.value)}-${padNumber(month.value)}-${
            year.value
        }`;
    };

    const onSubmit = (data) => {
        const { day, month, year } = data;
        const formattedDay = day.value === "all" ? null : day;
        const formattedMonth = month.value === "all" ? null : month;

        setFilter(formatFilterString(formattedDay, formattedMonth, year));
        onClose?.();
    };

    return (
        <div
            className="modal fade m-0 p-0"
            tabIndex={-1}
            id="date-filter-modal"
            aria-modal
        >
            <div className="modal-dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Filter</h5>
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        >
                            <em className="icon ni ni-cross" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="modal-body">
                            <div className="row g-3">
                                {[
                                    {
                                        name: "day",
                                        label: "Day",
                                        options: daysOptions,
                                    },
                                    {
                                        name: "month",
                                        label: "Month",
                                        options: monthsOptions,
                                    },
                                    {
                                        name: "year",
                                        label: "Year",
                                        options: yearsOptions,
                                    },
                                ].map(({ name, label, options }) => (
                                    <div key={name} className="col-md-4">
                                        <div className="form-group">
                                            <label htmlFor={name}>
                                                {label}
                                            </label>
                                            <Controller
                                                name={name}
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        options={options}
                                                        placeholder={`Select a ${label.toLowerCase()}`}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="modal-footer bg-light">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MachineStatusFilter;
